/**
 * PushNotificationPrompt - Prominent banner to encourage push notification adoption
 * Shows on homepage for logged-in users who haven't enabled push
 */
import React, { useState, useEffect } from "react";
import { Bell, BellRing, X, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../../utils";

const PushNotificationPrompt = ({ token, onSubscribe }) => {
  const [visible, setVisible] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check browser support
    const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }

    // Check if already dismissed recently (24 hours)
    const dismissedAt = localStorage.getItem("pushPromptDismissed");
    if (dismissedAt) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        setDismissed(true);
        return;
      }
    }

    // Check subscription status after a delay to let page load
    const timer = setTimeout(() => {
      if (token && supported && permission !== "denied") {
        checkSubscription();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [token, permission]);

  const checkSubscription = async () => {
    if (!token) return;
    
    try {
      const API = BACKEND_URL + "/api";
      const res = await fetch(`${API}/push/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setIsSubscribed(data.enabled);
      
      if (!data.enabled) {
        if (Notification.permission === "granted") {
          // User already ACCEPTED browser permission — quietly re-register
          // the subscription in the background instead of nagging with the
          // banner on every refresh.
          handleSubscribe(true);
        } else if (Notification.permission === "default") {
          setVisible(true);
        }
      }
    } catch (err) {
      console.error("Failed to check push status:", err);
    }
  };

  const handleSubscribe = async (silent = false) => {
    if (!isSupported) return;
    
    if (!silent) setLoading(true);
    try {
      // Request permission
      const permResult = await Notification.requestPermission();
      setPermission(permResult);
      
      if (permResult !== "granted") {
        if (!silent) toast.error("Please enable notifications in your browser settings");
        setLoading(false);
        return;
      }

      // Get VAPID key
      const API = BACKEND_URL + "/api";
      const keyRes = await fetch(`${API}/push/vapid-public-key`);
      const { publicKey } = await keyRes.json();
      
      // Convert VAPID key
      const urlBase64ToUint8Array = (base64String) => {
        const padding = "=".repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Wait for service worker
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send to server
      await fetch(`${API}/push/subscribe`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(subscription.toJSON())
      });

      setIsSubscribed(true);
      setVisible(false);
      if (!silent) toast.success("Push notifications enabled! You'll never miss a drop.");
      
      if (onSubscribe) onSubscribe();
      
    } catch (err) {
      console.error("Failed to subscribe:", err);
      if (!silent) toast.error("Failed to enable notifications. Please try again.");
    }
    setLoading(false);
  };

  const handleDismiss = () => {
    localStorage.setItem("pushPromptDismissed", Date.now().toString());
    setDismissed(true);
    setVisible(false);
  };

  // Don't show if not supported, already subscribed, denied, or dismissed
  if (!visible || !isSupported || isSubscribed || permission === "denied" || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-slide-up">
      <div className="bg-gradient-to-r from-orange-600 via-amber-500 to-orange-500 rounded-2xl p-1 shadow-2xl shadow-orange-500/30">
        <div className="bg-gray-900 rounded-xl p-4 relative overflow-hidden">
          {/* Background glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-orange-500/10 animate-pulse" />
          
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition z-10"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>

          <div className="relative z-10 flex items-start gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg animate-bounce-subtle">
              <BellRing className="text-black" size={28} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-white font-bold text-lg">Never Miss a Drop!</h3>
                <Sparkles className="text-amber-300" size={16} />
              </div>
              <p className="text-gray-400 text-sm mb-3">
                Get instant alerts for new drops, offers, and when it's your turn in the queue.
              </p>
              
              <button
                onClick={() => handleSubscribe(false)}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] disabled:opacity-70 disabled:hover:scale-100"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Enabling...
                  </>
                ) : (
                  <>
                    <Zap size={18} />
                    Enable Notifications
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Trust badges */}
          <div className="relative z-10 flex items-center justify-center gap-4 mt-3 pt-3 border-t border-gray-800">
            <span className="text-gray-500 text-xs flex items-center gap-1">
              <Bell size={12} />
              Instant alerts
            </span>
            <span className="text-gray-500 text-xs">•</span>
            <span className="text-gray-500 text-xs">No spam, ever</span>
            <span className="text-gray-500 text-xs">•</span>
            <span className="text-gray-500 text-xs">Unsubscribe anytime</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
        @keyframes bounce-subtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default PushNotificationPrompt;
